import { useState, useRef, useEffect } from "react";
import { renderMarkdown } from "@/utils/markdown";
import { useEmailContext } from "@/contexts/EmailContext";
import { emailReply } from "@/api/client";
import styles from "./EmailDraftCard.module.css";

interface EmailDraftData {
  from: string;
  subject: string;
  draftBody: string;
  summary?: string;
}

const MARKER = "<!--email-draft-->";

/** Try to parse a message that was injected with the email-draft marker. */
export function parseEmailDraft(content: string): EmailDraftData | null {
  if (!content.startsWith(MARKER)) return null;
  try {
    return JSON.parse(content.slice(MARKER.length));
  } catch {
    return null;
  }
}

/** Build a marked string to inject via `injectMessage`. */
export function encodeEmailDraft(data: EmailDraftData): string {
  return MARKER + JSON.stringify(data);
}

/** Extract plain text body from draft content (strip To:/Subject: header lines). */
function extractPlainBody(content: string): string {
  return content
    .replace(/^\*{0,2}To:\*{0,2}\s*.+$/m, "")
    .replace(/^\*{0,2}Subject:\*{0,2}\s*.+$/m, "")
    .trim();
}

export default function EmailDraftCard({ draftBody }: EmailDraftData) {
  const { emailMeta } = useEmailContext();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(() => extractPlainBody(draftBody));
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [editing]);

  const handleSend = async () => {
    if (!emailMeta) return;
    setStatus("sending");
    try {
      await emailReply(
        emailMeta.messageId,
        emailMeta.userId,
        emailMeta.tenantId,
        body,
      );
      setStatus("sent");
      setEditing(false);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.draftWrapper}>
        <div className={styles.draftLabel}>Draft Reply</div>
        {editing ? (
          <textarea
            ref={textareaRef}
            className={styles.draftTextarea}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
          />
        ) : (
          <div
            className={styles.draftBodyHtml}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(draftBody) }}
          />
        )}
        {status === "sent" ? (
          <div className={styles.sentBadge}>Sent</div>
        ) : (
          <div className={styles.buttonRow}>
            <button
              className={styles.btnEdit}
              onClick={() => setEditing((e) => !e)}
              disabled={status === "sending"}
              data-attr="email-draft-edit"
            >
              {editing ? "Preview" : "Edit"}
            </button>
            <button
              className={styles.btnSend}
              onClick={handleSend}
              disabled={status === "sending" || !emailMeta}
              data-attr="email-draft-send"
            >
              {status === "sending" ? "Sending…" : "Send"}
            </button>
          </div>
        )}
        {status === "error" && (
          <div className={styles.errorText}>Failed to send. Try again.</div>
        )}
      </div>
    </div>
  );
}
